import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Layout from '../components/Layout';
import CompanyPicker from '../components/CompanyPicker';
import AddressForm from '../components/AddressForm';
import TagPicker from '../components/TagPicker';
import {api} from '../../api';

const PHONE_TYPES = ['WORK', 'CELL', 'HOME'];
const EMAIL_TYPES = ['WORK', 'PERSONAL'];

export default function AddContact() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ firstName: '', lastName: '', emails: [], phones: [], addresses: [], notes: [], companies: [], tags: [], sourceId: '', status: 'ACTIVE' });
  const [sources, setSources] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    api(`/contact-sources`).then(r => r.json()).then(setSources).catch(() => {});
  }, []);

  const addEmail = () => setForm(f => ({ ...f, emails: [...f.emails, { email: '', type: 'PERSONAL' }] }));
  const updateEmail = (i, field, v) => setForm(f => ({ ...f, emails: f.emails.map((e, idx) => idx === i ? { ...e, [field]: v } : e) }));
  const removeEmail = (i) => setForm(f => ({ ...f, emails: f.emails.filter((_, idx) => idx !== i) }));

  const addPhone = () => setForm(f => ({ ...f, phones: [...f.phones, { phone: '', type: 'CELL' }] }));
  const updatePhone = (i, field, v) => setForm(f => ({ ...f, phones: f.phones.map((p, idx) => idx === i ? { ...p, [field]: v } : p) }));
  const removePhone = (i) => setForm(f => ({ ...f, phones: f.phones.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = {
      ...form,
      emails: form.emails.filter(e => e.email),
      phones: form.phones.filter(p => p.phone),
      addresses: form.addresses,
      notes: form.notes.filter(n => n.body),
      companies: form.companies.map(c => ({ companyId: c.companyId, title: c.title || null, emails: (c.emails || []).filter(e => e), phones: (c.phones || []).filter(p => p) })),
      tagIds: form.tags.map(t => t.id),
      sourceId: form.sourceId || null
    };
    setError('');
    const res = await api(`/contacts`, { method: 'POST', body: JSON.stringify(body) });
    if (res.ok) {
      const contact = await res.json();
      navigate(`/crm/contacts/${contact.id}`);
    } else {
      const data = await res.json().catch(() => null);
      setError(data?.error || 'Failed to create contact. Please try again.');
    }
  };

  return (
    <Layout>
      <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Add Contact</h1>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex justify-between items-center">
            {error}
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 ml-4">&times;</button>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input type="text" required value={form.firstName} onChange={(e) => setForm({...form, firstName: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input type="text" required value={form.lastName} onChange={(e) => setForm({...form, lastName: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Emails</label>
            <div className="space-y-2">
              {form.emails.map((e, i) => (
                <div key={i} className="flex gap-2">
                  <input type="email" placeholder="Email address" value={e.email} onChange={(ev) => updateEmail(i, 'email', ev.target.value)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  <select value={e.type} onChange={(ev) => updateEmail(i, 'type', ev.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600">
                    {EMAIL_TYPES.map(t => <option key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</option>)}
                  </select>
                  <button type="button" onClick={() => removeEmail(i)} className="text-gray-400 hover:text-blue-600 px-2">&times;</button>
                </div>
              ))}
              <button type="button" onClick={addEmail} className="text-sm text-blue-600 hover:text-blue-800">+ Add email</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phones</label>
            <div className="space-y-2">
              {form.phones.map((p, i) => (
                <div key={i} className="flex gap-2">
                  <input type="text" placeholder="Phone number" value={p.phone} onChange={(ev) => updatePhone(i, 'phone', ev.target.value)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  <select value={p.type} onChange={(ev) => updatePhone(i, 'type', ev.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600">
                    {PHONE_TYPES.map(t => <option key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</option>)}
                  </select>
                  <button type="button" onClick={() => removePhone(i)} className="text-gray-400 hover:text-blue-600 px-2">&times;</button>
                </div>
              ))}
              <button type="button" onClick={addPhone} className="text-sm text-blue-600 hover:text-blue-800">+ Add phone</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Addresses</label>
            <AddressForm addresses={form.addresses} onChange={(addresses) => setForm({...form, addresses})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <div className="space-y-2">
              {form.notes.map((n, i) => (
                <div key={i} className="flex gap-2">
                  <textarea placeholder="Note" value={n.body} onChange={(e) => setForm(f => ({ ...f, notes: f.notes.map((x, idx) => idx === i ? { ...x, body: e.target.value } : x) }))} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" rows={2} />
                  <button type="button" onClick={() => setForm(f => ({ ...f, notes: f.notes.filter((_, idx) => idx !== i) }))} className="text-gray-400 hover:text-blue-600 px-2">&times;</button>
                </div>
              ))}
              <button type="button" onClick={() => setForm(f => ({ ...f, notes: [...f.notes, { body: '' }] }))} className="text-sm text-blue-600 hover:text-blue-800">+ Add note</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Companies</label>
            <CompanyPicker selected={form.companies} onChange={(companies) => setForm({...form, companies})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
            <TagPicker selected={form.tags} onChange={(tags) => setForm({...form, tags})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <select value={form.sourceId} onChange={(e) => setForm({...form, sourceId: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600">
              <option value="">No source</option>
              {sources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600">
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => navigate('/crm/contacts')} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save</button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
